import { useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Textarea } from '../../components/ui';
import { escapeHtml, renderBodyHtml } from '../gmail/mailSend';
import { PHOTO_SIZES, photoLineWidth, photoMark, withPhotoWidth } from '../gmail/emailPhotos';
import { insertAt, insertLink, toggleBullets, toggleInlineMark } from './emailEditing';
import type { Edit, InlineMark } from './emailEditing';
import './emailEditor.css';

/**
 * The email body box, with the formatting toolbar and a preview.
 *
 * Hanneza, testing the Hub's email on herself (3 Sep): "we currently lack the
 * ability to format emails in the Compose tab (no font changes, bolds,
 * italics, or embedded hyperlinks)". Kyu's scope: bold, italic, underline,
 * bullets, links.
 *
 * The text stays plain text with marks in it (docs/rich-email-design-note.md,
 * choice B). The toolbar types the marks for you — B wraps the selection in
 * `**`, and so on — and Preview shows the email the way it will arrive. Same
 * component on Compose, on Reply and in the Copy Library, so a set written in
 * one place reads the same in the others.
 *
 * On the preview: the Hub's rule is that INBOUND HTML is never rendered
 * (mailThread.ts says why). The preview is not inbound — it is the HTML this
 * Hub is about to send, built by renderBodyHtml from text it escaped itself,
 * with the only tags being the five it writes. Rendering our own output is
 * how the person checks it, and refusing would leave them sending blind.
 */

/**
 * The email as it will arrive, with the photos the person can see now.
 *
 * renderBodyHtml only draws a cid: photo, because only a cid names a part of
 * the message. For the preview, a photo: or drive: reference is stood in for
 * by a temporary cid, rendered, and then swapped for the picture the browser
 * has (an object URL) or a labelled box when it has none — a copy set's
 * Drive photo, for instance, which is read only at send time.
 */
export function previewHtml(body: string, photoPreview?: (ref: string) => string | null): string {
  const refs: string[] = [];
  const staged = body.replace(
    /!\[([^[\]\n]*)\]\(((?:photo|drive):[^()\n]+)\)/g,
    (_whole, caption: string, ref: string) => {
      refs.push(ref);
      return `![${caption}](cid:preview-${String(refs.length - 1)})`;
    },
  );
  // The size attributes renderBodyHtml wrote (width="…" style="…") are kept
  // on the preview picture, so a Medium photo previews at Medium.
  return renderBodyHtml(staged).replace(
    /<img src="cid:preview-(\d+)" alt="([^"]*)"((?: [a-z]+="[^"]*")*)>/g,
    (_m, index: string, alt: string, sizing: string) => {
      const ref = refs[Number(index)] ?? '';
      const url = photoPreview?.(ref) ?? null;
      if (url !== null) {
        return `<img src="${escapeHtml(url)}" alt="${alt}"${sizing} class="email-editor-photo">`;
      }
      const label = alt === '' ? 'Photo' : `Photo: ${alt}`;
      return `<span class="email-editor-photo-box">${label}</span>`;
    },
  );
}

export function EmailBodyEditor({
  label,
  labelHidden,
  value,
  onChange,
  rows = 10,
  placeholder,
  tokens = [],
  hint,
  onPhoto,
  photoPreview,
}: {
  label: string;
  labelHidden?: boolean | undefined;
  value: string;
  onChange: (next: string) => void;
  rows?: number | undefined;
  placeholder?: string | undefined;
  /** Snippets to drop at the caret — the Copy Library's {{first_name}} and friends. */
  tokens?: readonly string[] | undefined;
  hint?: ReactNode;
  /**
   * Photos in the body (drop B). The caller owns where a picture comes from —
   * a file picked on Compose, an upload to Drive in the Copy Library — and
   * hands back its reference and a caption to start from, or null if the
   * person cancelled. Absent, there is no Photo button.
   */
  onPhoto?: (() => Promise<{ ref: string; caption: string } | null>) | undefined;
  /**
   * For the preview: a picture the browser can show for a photo: or drive:
   * reference (an object URL for a picked file). Null shows a labelled box.
   */
  photoPreview?: ((ref: string) => string | null) | undefined;
}) {
  const area = useRef<HTMLTextAreaElement>(null);
  const [preview, setPreview] = useState(false);
  // The Photo size picker. It shows the size of the photo the caret is on, or
  // the size the next photo will get when the caret is on ordinary text.
  const [nextWidth, setNextWidth] = useState<number | null>(null);
  const [caret, setCaret] = useState(0);

  const lineStart = value.lastIndexOf('\n', Math.max(0, caret - 1)) + 1;
  const lineEndAt = value.indexOf('\n', lineStart);
  const lineEnd = lineEndAt === -1 ? value.length : lineEndAt;
  const caretLineWidth = photoLineWidth(value.slice(lineStart, lineEnd));
  const shownWidth = caretLineWidth === undefined ? nextWidth : caretLineWidth;

  function trackCaret() {
    const el = area.current;
    if (el !== null) setCaret(el.selectionStart);
  }

  function chooseSize(raw: string) {
    const width = raw === '' ? null : Number(raw);
    setNextWidth(width);
    if (caretLineWidth === undefined) return;
    // The caret is on a photo line: resize that photo, keep the caret on it.
    const line = value.slice(lineStart, lineEnd);
    const resized = withPhotoWidth(line, width);
    onChange(value.slice(0, lineStart) + resized + value.slice(lineEnd));
    const el = area.current;
    if (el !== null) {
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(lineStart + resized.length, lineStart + resized.length);
      });
    }
  }

  function apply(edit: (text: string, start: number, end: number) => Edit) {
    const el = area.current;
    const start = el === null ? value.length : el.selectionStart;
    const end = el === null ? value.length : el.selectionEnd;
    const next = edit(value, start, end);
    onChange(next.text);
    if (el !== null) {
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(next.start, next.end);
      });
    }
  }

  const mark = (which: InlineMark) => apply((t, s, e) => toggleInlineMark(t, s, e, which));

  async function addPhoto() {
    if (onPhoto === undefined) return;
    const picked = await onPhoto();
    if (picked === null) return;
    // On its own line: a picture in an email is a block, not a word.
    apply((t, s, e) => {
      const before = t.slice(0, s);
      const lead = before === '' || before.endsWith('\n') ? '' : '\n';
      return insertAt(t, s, e, `${lead}${photoMark(picked.caption, picked.ref, nextWidth)}\n`);
    });
  }

  // The shortcuts every writing tool has. Handled here rather than left to
  // the browser, which would otherwise do nothing in a textarea.
  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
    const key = event.key.toLowerCase();
    if (key === 'b') mark('bold');
    else if (key === 'i') mark('italic');
    else if (key === 'u') mark('underline');
    else if (key === 'k') apply(insertLink);
    else return;
    event.preventDefault();
  }

  return (
    <div className="email-editor">
      <div className="email-editor-toolbar" role="toolbar" aria-label="Formatting">
        <button
          type="button"
          className="email-editor-tool email-editor-tool--bold"
          title="Bold (⌘B)"
          aria-label="Bold"
          disabled={preview}
          onClick={() => mark('bold')}
        >
          B
        </button>
        <button
          type="button"
          className="email-editor-tool email-editor-tool--italic"
          title="Italic (⌘I)"
          aria-label="Italic"
          disabled={preview}
          onClick={() => mark('italic')}
        >
          I
        </button>
        <button
          type="button"
          className="email-editor-tool email-editor-tool--underline"
          title="Underline (⌘U)"
          aria-label="Underline"
          disabled={preview}
          onClick={() => mark('underline')}
        >
          U
        </button>
        <button
          type="button"
          className="email-editor-tool"
          title="Bullet list"
          aria-label="Bullet list"
          disabled={preview}
          onClick={() => apply(toggleBullets)}
        >
          • List
        </button>
        <button
          type="button"
          className="email-editor-tool"
          title="Link (⌘K)"
          aria-label="Link"
          disabled={preview}
          onClick={() => apply(insertLink)}
        >
          Link
        </button>
        {onPhoto === undefined ? null : (
          <>
            <button
              type="button"
              className="email-editor-tool"
              title="Photo in the body"
              aria-label="Photo"
              disabled={preview}
              onClick={() => void addPhoto()}
            >
              Photo
            </button>
            <select
              className="email-editor-tool email-editor-size"
              aria-label="Photo size"
              title="Photo size — for the photo under the cursor, or the next one you add"
              disabled={preview}
              value={shownWidth === null ? '' : String(shownWidth)}
              onChange={(event) => chooseSize(event.target.value)}
            >
              {PHOTO_SIZES.map((size) => (
                <option key={size.label} value={size.width === null ? '' : String(size.width)}>
                  {size.label}
                </option>
              ))}
            </select>
          </>
        )}
        {tokens.length === 0 ? null : <span className="email-editor-divider" aria-hidden="true" />}
        {tokens.map((token) => (
          <button
            key={token}
            type="button"
            className="email-editor-tool email-editor-tool--token"
            disabled={preview}
            onClick={() => apply((t, s, e) => insertAt(t, s, e, token))}
          >
            {token}
          </button>
        ))}
        <button
          type="button"
          className={`email-editor-tool email-editor-preview-toggle${preview ? ' email-editor-tool--active' : ''}`}
          aria-pressed={preview}
          onClick={() => setPreview(!preview)}
        >
          {preview ? 'Edit' : 'Preview'}
        </button>
      </div>

      {preview ? (
        <div className="email-editor-preview" aria-label="Preview">
          {value.trim() === '' ? (
            <p className="tracker-field-note">Nothing to preview yet.</p>
          ) : (
            <div
              className="email-editor-preview-body"
              // Our own output, from text we escaped: see the note at the top.
              dangerouslySetInnerHTML={{ __html: previewHtml(value, photoPreview) }}
            />
          )}
          {hint === undefined ? null : <p className="ui-field-hint">{hint}</p>}
        </div>
      ) : (
        <Textarea
          ref={area}
          label={label}
          labelHidden={labelHidden}
          value={value}
          rows={rows}
          placeholder={placeholder}
          hint={hint}
          onChange={(event) => {
            onChange(event.target.value);
            trackCaret();
          }}
          onKeyDown={onKeyDown}
          onKeyUp={trackCaret}
          onClick={trackCaret}
          onSelect={trackCaret}
        />
      )}
    </div>
  );
}
